export const PALADIN_ERROR_CODES = {
  VALIDATION_FAILED: "VALIDATION_FAILED",
  PERMISSIONS_FAILED: "PERMISSIONS_FAILED",
  BAD_CREDENTIALS: "BAD_CREDENTIALS",
  NOT_LOGGED_IN: "NOT_LOGGED_IN",
  NO_ACCOUNT_ASSOCIATED_TO_THIS_SSO_USER_ERROR:
    "NO_ACCOUNT_ASSOCIATED_TO_THIS_SSO_USER_ERROR",
  PHONE_VERIFICATION_PENDING_REVIEW: "PHONE_VERIFICATION_PENDING_REVIEW",
  FORBIDDEN: "FORBIDDEN",
  INTERNAL_SERVER_ERROR: "INTERNAL_SERVER_ERROR",
  APPOINTMENT_ALREADY_BOOKED_BY_COLLABORATOR:
    "APPOINTMENT_ALREADY_BOOKED_BY_COLLABORATOR",
  BOOKING_SLOT_NO_LONGER_AVAILABLE: "BOOKING_SLOT_NO_LONGER_AVAILABLE",
  CONFLICTING_ABSENCE_SLOT: "CONFLICTING_ABSENCE_SLOT",
  CONFLICTING_ACTIVITY: "CONFLICTING_ACTIVITY",
  ABSENCE_SLOT_START_TOO_SOON: "ABSENCE_SLOT_START_TOO_SOON",
  ACTIVITY_START_DATE_IN_THE_PAST: "ACTIVITY_START_DATE_IN_THE_PAST",
  EMAIL_NOT_AVAILABLE: "EMAIL_NOT_AVAILABLE",
  FIELD_LOCKED_BY_HRIS: "FIELD_LOCKED_BY_HRIS",
  PHONE_NUMBER_REQUIRED: "PHONE_NUMBER_REQUIRED",
  CONFLICTING_BOOKING_SLOT_OF_ANOTHER_TYPE:
    "CONFLICTING_BOOKING_SLOT_OF_ANOTHER_TYPE",
  VISIT_BOOKING_SLOT_NOT_ALLOWED_FOR_NON_MEDICAL_STAFF:
    "VISIT_BOOKING_SLOT_NOT_ALLOWED_FOR_NON_MEDICAL_STAFF",
  APPOINTMENT_OUTSIDE_OF_BOOKING_SLOT_INTERVAL:
    "APPOINTMENT_OUTSIDE_OF_BOOKING_SLOT_INTERVAL",
  BOOKING_SLOT_CANT_UPDATE_TYPE_IF_APPOINTMENT:
    "BOOKING_SLOT_CANT_UPDATE_TYPE_IF_APPOINTMENT",
  BOOKING_SLOT_CANT_DELETE_IF_APPOINTMENT:
    "BOOKING_SLOT_CANT_DELETE_IF_APPOINTMENT",
  CANT_CREATE_CHECKUP_FOR_NO_SHOW_APPOINTMENT:
    "CANT_CREATE_CHECKUP_FOR_NO_SHOW_APPOINTMENT",
  CONFLICTING_APPOINTMENT: "CONFLICTING_APPOINTMENT",
  APPOINTMENT_INTERVAL_NOT_IN_BOOKING_SLOT_INTERVAL:
    "APPOINTMENT_INTERVAL_NOT_IN_BOOKING_SLOT_INTERVAL",
  APPOINTMENT_START_DATE_IN_THE_PAST: "APPOINTMENT_START_DATE_IN_THE_PAST",
  MEMBERSHIP_NOT_STARTED: "MEMBERSHIP_NOT_STARTED",
  CHECKUP_ALREADY_EXISTS_FOR_APPOINTMENT:
    "CHECKUP_ALREADY_EXISTS_FOR_APPOINTMENT",
  EMPTY_FILE: "EMPTY_FILE",
  RISK_NOT_FOUND: "RISK_NOT_FOUND",
  RISK_NOT_ASSOCIATED_TO_MEMBER_BRANCH: "RISK_NOT_ASSOCIATED_TO_MEMBER_BRANCH",
  INTEGRATION_NOT_FOUND: "INTEGRATION_NOT_FOUND",
  INTEGRATION_GENERIC_ERROR: "INTEGRATION_GENERIC_ERROR",
  TIMEOUT: "TIMEOUT",
  MISSING_EXPECTED_INFO_FOR_BOOKING: "MISSING_EXPECTED_INFO_FOR_BOOKING",
  JOB_TITLE_REQUIRED_FOR_ABSENCE_DECLARATION:
    "JOB_TITLE_REQUIRED_FOR_ABSENCE_DECLARATION",
  DOWNLOAD_LINK_EXPIRED: "DOWNLOAD_LINK_EXPIRED",
  APPOINTMENT_WITHOUT_DOCUMENTS: "APPOINTMENT_WITHOUT_DOCUMENTS",
  DMST_EXPORT_CONTENTS_CHANGED: "DMST_EXPORT_CONTENTS_CHANGED",
  COMPANY_BRANCH_ALREADY_EXISTS: "COMPANY_BRANCH_ALREADY_EXISTS",
  REPRESENTATIVE_IS_ALREADY_A_PATIENT: "REPRESENTATIVE_IS_ALREADY_A_PATIENT",
  WORK_UNIT_NAME_ALREADY_EXISTS: "WORK_UNIT_NAME_ALREADY_EXISTS",
  WORK_UNIT_LIMIT_REACHED: "WORK_UNIT_LIMIT_REACHED",
  WORK_UNIT_RELATION_TARGET_NOT_FOUND: "WORK_UNIT_RELATION_TARGET_NOT_FOUND",
  WEIGHTED_RISK_ALREADY_DECLARED: "WEIGHTED_RISK_ALREADY_DECLARED",
  WEIGHTED_RISK_WORK_UNIT_NOT_FOUND: "WEIGHTED_RISK_WORK_UNIT_NOT_FOUND",
  MEDICAL_ATTESTATION_NOT_READABLE: "MEDICAL_ATTESTATION_NOT_READABLE",
} as const;

const paladinErrorCodesSet = new Set(Object.values(PALADIN_ERROR_CODES));
export type PaladinErrorCode =
  (typeof PALADIN_ERROR_CODES)[keyof typeof PALADIN_ERROR_CODES];

export const isPaladinErrorCode = (
  errorCode: unknown,
): errorCode is PaladinErrorCode => {
  return (
    typeof errorCode === "string" &&
    paladinErrorCodesSet.has(errorCode as PaladinErrorCode)
  );
};

export const PALADIN_API_ERRORS = {
  ValidationFailed: {
    statusCode: 400 as const,
    errorCode: PALADIN_ERROR_CODES.VALIDATION_FAILED,
  },
  PermissionsFailed: {
    statusCode: 403 as const,
    errorCode: PALADIN_ERROR_CODES.PERMISSIONS_FAILED,
  },
  BadCredentials: {
    statusCode: 401 as const,
    errorCode: PALADIN_ERROR_CODES.BAD_CREDENTIALS,
  },
  NotLoggedIn: {
    statusCode: 401 as const,
    errorCode: PALADIN_ERROR_CODES.NOT_LOGGED_IN,
  },
  NoAccountAssociatedToThisSsoUserError: {
    statusCode: 403 as const,
    errorCode: PALADIN_ERROR_CODES.NO_ACCOUNT_ASSOCIATED_TO_THIS_SSO_USER_ERROR,
  },
  PhoneVerificationPendingReview: {
    statusCode: 403 as const,
    errorCode: PALADIN_ERROR_CODES.PHONE_VERIFICATION_PENDING_REVIEW,
  },
  Forbidden: {
    statusCode: 403 as const,
    errorCode: PALADIN_ERROR_CODES.FORBIDDEN,
  },
  Timeout: {
    statusCode: 408 as const,
    errorCode: PALADIN_ERROR_CODES.TIMEOUT,
  },
  InternalServerError: {
    statusCode: 500 as const,
    errorCode: PALADIN_ERROR_CODES.INTERNAL_SERVER_ERROR,
  },
  AppointmentAlreadyBookedByCollaborator: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.APPOINTMENT_ALREADY_BOOKED_BY_COLLABORATOR,
  },
  BookingSlotNoLongerAvailable: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.BOOKING_SLOT_NO_LONGER_AVAILABLE,
  },
  ConflictingAbsenceSlot: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.CONFLICTING_ABSENCE_SLOT,
  },
  ConflictingActivity: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.CONFLICTING_ACTIVITY,
  },
  AbsenceSlotStartTooSoon: {
    statusCode: 403 as const,
    errorCode: PALADIN_ERROR_CODES.ABSENCE_SLOT_START_TOO_SOON,
  },
  ActivityStartDateInThePast: {
    statusCode: 400 as const,
    errorCode: PALADIN_ERROR_CODES.ACTIVITY_START_DATE_IN_THE_PAST,
  },
  EmailNotAvailable: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.EMAIL_NOT_AVAILABLE,
  },
  FieldLockedByHris: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.FIELD_LOCKED_BY_HRIS,
  },
  PhoneNumberRequired: {
    statusCode: 400 as const,
    errorCode: PALADIN_ERROR_CODES.PHONE_NUMBER_REQUIRED,
  },
  ConflictingBookingSlot: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.CONFLICTING_BOOKING_SLOT_OF_ANOTHER_TYPE,
  },
  VisitBookingSlotNotAllowedForNonMedicalStaff: {
    statusCode: 400 as const,
    errorCode:
      PALADIN_ERROR_CODES.VISIT_BOOKING_SLOT_NOT_ALLOWED_FOR_NON_MEDICAL_STAFF,
  },
  ConflictingAppointment: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.CONFLICTING_APPOINTMENT,
  },
  AppointmentOutsideOfBookingSlotInterval: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.APPOINTMENT_OUTSIDE_OF_BOOKING_SLOT_INTERVAL,
  },
  BookingSlotCantChangeTypeIfAppointment: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.BOOKING_SLOT_CANT_UPDATE_TYPE_IF_APPOINTMENT,
  },
  BookingSlotCantDeleteIfAppointment: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.BOOKING_SLOT_CANT_DELETE_IF_APPOINTMENT,
  },
  CantCreateCheckupForNoShowAppointment: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.CANT_CREATE_CHECKUP_FOR_NO_SHOW_APPOINTMENT,
  },
  AppointmentIntervalNotInBookingSlotInterval: {
    statusCode: 409 as const,
    errorCode:
      PALADIN_ERROR_CODES.APPOINTMENT_INTERVAL_NOT_IN_BOOKING_SLOT_INTERVAL,
  },
  AppointmentStartDateInThePast: {
    statusCode: 400 as const,
    errorCode: PALADIN_ERROR_CODES.APPOINTMENT_START_DATE_IN_THE_PAST,
  },
  MembershipNotStarted: {
    statusCode: 403 as const,
    errorCode: PALADIN_ERROR_CODES.MEMBERSHIP_NOT_STARTED,
  },
  CheckupAlreadyExistsForAppointment: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.CHECKUP_ALREADY_EXISTS_FOR_APPOINTMENT,
  },
  EmptyFile: {
    statusCode: 400 as const,
    errorCode: PALADIN_ERROR_CODES.EMPTY_FILE,
  },
  RiskNotFound: {
    statusCode: 404 as const,
    errorCode: PALADIN_ERROR_CODES.RISK_NOT_FOUND,
  },
  RiskNotAssociatedToMemberBranch: {
    statusCode: 400 as const,
    errorCode: PALADIN_ERROR_CODES.RISK_NOT_ASSOCIATED_TO_MEMBER_BRANCH,
  },
  IntegrationNotFound: {
    statusCode: 404 as const,
    errorCode: PALADIN_ERROR_CODES.INTEGRATION_NOT_FOUND,
  },
  IntegrationGenericError: {
    statusCode: 503 as const,
    errorCode: PALADIN_ERROR_CODES.INTEGRATION_GENERIC_ERROR,
  },
  MissingExpectedInfoForBooking: {
    statusCode: 400 as const,
    errorCode: PALADIN_ERROR_CODES.MISSING_EXPECTED_INFO_FOR_BOOKING,
  },
  JobTitleRequiredForAbsenceDeclaration: {
    statusCode: 400 as const,
    errorCode: PALADIN_ERROR_CODES.JOB_TITLE_REQUIRED_FOR_ABSENCE_DECLARATION,
  },
  DownloadLinkExpired: {
    statusCode: 410 as const,
    errorCode: PALADIN_ERROR_CODES.DOWNLOAD_LINK_EXPIRED,
  },
  AppointmentWithoutDocuments: {
    statusCode: 404 as const,
    errorCode: PALADIN_ERROR_CODES.APPOINTMENT_WITHOUT_DOCUMENTS,
  },
  DmstExportContentsChanged: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.DMST_EXPORT_CONTENTS_CHANGED,
  },
  CompanyBranchAlreadyExists: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.COMPANY_BRANCH_ALREADY_EXISTS,
  },
  RepresentativeIsAlreadyAPatient: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.REPRESENTATIVE_IS_ALREADY_A_PATIENT,
  },
  WorkUnitNameAlreadyExists: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.WORK_UNIT_NAME_ALREADY_EXISTS,
  },
  WorkUnitLimitReached: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.WORK_UNIT_LIMIT_REACHED,
  },
  WorkUnitRelationTargetNotFound: {
    statusCode: 404 as const,
    errorCode: PALADIN_ERROR_CODES.WORK_UNIT_RELATION_TARGET_NOT_FOUND,
  },
  WeightedRiskAlreadyDeclared: {
    statusCode: 409 as const,
    errorCode: PALADIN_ERROR_CODES.WEIGHTED_RISK_ALREADY_DECLARED,
  },
  WeightedRiskWorkUnitNotFound: {
    statusCode: 404 as const,
    errorCode: PALADIN_ERROR_CODES.WEIGHTED_RISK_WORK_UNIT_NOT_FOUND,
  },
  MedicalAttestationNotReadable: {
    statusCode: 422 as const,
    errorCode: PALADIN_ERROR_CODES.MEDICAL_ATTESTATION_NOT_READABLE,
  },
};

export type PaladinApiErrors = typeof PALADIN_API_ERRORS;
export type PaladinErrorName = keyof typeof PALADIN_API_ERRORS;
export type PaladinApiError = PaladinApiErrors[PaladinErrorName];

export function isPaladinApiError<ErrorName extends PaladinErrorName>(
  response: unknown,
  errorName: ErrorName,
): response is PaladinApiErrors[ErrorName];
export function isPaladinApiError(
  response: unknown,
): response is PaladinApiError;
export function isPaladinApiError<
  ErrorName extends PaladinErrorName = PaladinErrorName,
>(
  response: unknown,
  errorName?: ErrorName,
): response is PaladinApiErrors[ErrorName] {
  return (
    typeof response === "object" &&
    response !== null &&
    "statusCode" in response &&
    typeof response.statusCode === "number" &&
    "errorCode" in response &&
    isPaladinErrorCode(response.errorCode) &&
    (errorName === undefined ||
      (response.statusCode === PALADIN_API_ERRORS[errorName].statusCode &&
        response.errorCode === PALADIN_API_ERRORS[errorName].errorCode))
  );
}
